import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { VALID_REG } from "../../../constants/ValidationRules";
import InputInline from "../../../components/InputInline";

interface EmailInputFieldProps {
  value?: string | number;
  onChange?: (value: string, error: boolean) => void;
  onFinish?: (value: string, error: boolean) => void;
  placeholder?: string;
  error?: boolean;
  required?: boolean;
  border?: boolean;
  fieldId?: string;
  showError?: boolean;
  isGrid?: boolean;
  variant?: "default" | "inline";
}

const EmailInputField: React.FC<EmailInputFieldProps> = ({
  value = "",
  onChange = () => {},
  onFinish = () => {},
  placeholder = "",
  error = false,
  required = false,
  border = true,
  fieldId = "email",
  showError = false,
  isGrid = false,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const [, setInputValue] = useState(value);

  const formatEmail = (email: string) => {
    if (!email.trim()) return email;

    return email.trim().toLowerCase();
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    const isValid = required
      ? newValue.trim() !== ""
      : newValue.trim() === "" || VALID_REG?.[fieldId]?.reg.test(newValue);
    onChange(newValue, !isValid);
  };

  const handleFinish = (finalValue: string, save?: boolean) => {
    // Allow blur to trigger save (save will be true from InputInline's onBlur)
    // Only skip if explicitly false
    if (save === false) {
      return;
    }

    let processedValue = formatEmail(finalValue);

    // Validate using the finalValue, not the state
    const isValid = required
      ? processedValue.trim() !== ""
      : processedValue.trim() === "" || VALID_REG?.[fieldId]?.reg.test(processedValue);

    setInputValue(processedValue);
    onFinish(processedValue, !isValid);
  };

  const renderError = () => {
    if (error && required) {
      return null;
    }
    if (error) {
      return (
        <p className="mt-2 text-sm text-red-600">
          {required
            ? t("This field is required")
            : VALID_REG?.[fieldId]?.mes || t("Invalid email format")}
        </p>
      );
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <>
      <InputInline
        type="email"
        placeholder={isGrid ? "" : placeholder || "example@domain.com"}
        value={value}
        border={isGrid ? false : border}
        onChange={handleInputChange}
        onFinish={handleFinish}
        variant={variant}
      />
      {error && showError && renderError()}
    </>
  );
};

export default EmailInputField;
